import { useEffect, useState } from 'react';

import { useParams } from 'next/navigation';

import { HOTEL_SEARCH_QUERY_PARAM_KEYS } from '@components/views/hotel/constants';
import { useFindHotelLocationBySlug } from '@components/views/hotel/hooks/hotel.query';
import { convertQueryRoomToHotelRooms } from '@components/views/hotel/utils';
import {
  convertDateToMomentObject,
  HOTEL_SEARCH_DATE_FORMAT,
} from '@utils/dateUtil';

import { IHotelSearchBarState } from '../types/hotel';

/**
 * Convert the query params to the hotel search bar state
 * @param query object
 * @returns Partial<IHotelSearchBarState>
 */
export const useHotelQuery = (query, enabled) => {
  const [values, setValues] = useState<Partial<IHotelSearchBarState>>();
  const params = useParams();
  const {
    [HOTEL_SEARCH_QUERY_PARAM_KEYS.checkInDate]: checkInDate,
    [HOTEL_SEARCH_QUERY_PARAM_KEYS.checkOutDate]: checkOutDate,
    [HOTEL_SEARCH_QUERY_PARAM_KEYS.room]: rooms,
  } = query;

  const { data: hotelLocation } = useFindHotelLocationBySlug(
    params?.slug as string,
    enabled
  );

  /**
   * Set the hotel location when change the hotel slug
   */
  useEffect(() => {
    if (!hotelLocation) return;
    setValues((previousValues) => ({
      ...previousValues,
      location: hotelLocation,
    }));
  }, [hotelLocation]);

  /**
   * Set the check in and out dates when query params are present
   */
  useEffect(() => {
    if (checkInDate && checkOutDate) {
      setValues((previousValues) => ({
        ...previousValues,
        checkDates: {
          startDate: convertDateToMomentObject(
            checkInDate,
            HOTEL_SEARCH_DATE_FORMAT
          ),
          endDate: convertDateToMomentObject(
            checkOutDate,
            HOTEL_SEARCH_DATE_FORMAT
          ),
        },
      }));
    }
  }, [checkInDate, checkOutDate]);

  /**
   * Set the rooms when query params are present
   */
  useEffect(() => {
    if (rooms) {
      const preparedRooms = convertQueryRoomToHotelRooms(rooms?.split(','));

      setValues((previousValues) => ({
        ...previousValues,
        rooms: preparedRooms,
      }));
    }
  }, [rooms]);

  return values;
};
